// src/config/characters.ts — 11 Rig_Medium karakter: GLB yolu, doku sınıfı, ağırlık çarpanı (DESIGN §5), idle seti, taunt klibi.
// Kaynak: assets/viewer/data/catalog.json (characters[], textures[]); yollar /assets/... (public/assets: depo içinde
// gerçek klasör, yalnız kullanılan dosyalar — yeni karakter eklerken `node tools/collect-assets.mjs` çalıştır).
import type { CharId, WeaponKind } from '../core/types.ts';
import { CLIPS } from './clipTable.ts';

/** Doku/materyal sınıfı: aynı atlası paylaşan karakter + proplar tek MeshStandardMaterial kullanır (ARCHITECTURE §6). */
export type TexClass = 'knight' | 'barbarian' | 'mage' | 'rogue' | 'ranger' | 'skeleton' | 'mannequin';
export const TEXTURE_URLS: Readonly<Record<TexClass, string>> = {
  knight: '/assets/KayKit_Adventurers_2.0_FREE/Textures/knight_texture.png',
  barbarian: '/assets/KayKit_Adventurers_2.0_FREE/Textures/barbarian_texture.png',
  mage: '/assets/KayKit_Adventurers_2.0_FREE/Textures/mage_texture.png',
  rogue: '/assets/KayKit_Adventurers_2.0_FREE/Textures/rogue_texture.png',
  ranger: '/assets/KayKit_Adventurers_2.0_FREE/Textures/ranger_texture.png',
  skeleton: '/assets/KayKit_Skeletons_1.1_FREE/texture/skeleton_texture.png',
  mannequin: '/assets/KayKit_Character_Animations_1.1/Mannequin Character/Textures/mannequin_texture.png',
};

/** Silah sınıfına göre idle klibi (ANIMATION §2.1): silahsız Melee_Unarmed_Idle; 1H/dual/kalkan Idle_A; 2H/asa Melee_2H_Idle. */
export interface IdleSet { unarmed: string; oneHand: string; twoHand: string; /** 30 s'de bir tek tur (yalnız Idle_A seti) */ variant: string }
export const IDLE_SET_DEFAULT: IdleSet = { unarmed: CLIPS.MELEE_UNARMED_IDLE, oneHand: CLIPS.IDLE_A, twoHand: CLIPS.MELEE_2H_IDLE, variant: CLIPS.IDLE_B };
/** Silah türü → idle klibi seçimi (animMap). */
export function idleClipFor(set: IdleSet, weapon: WeaponKind, hasShield: boolean): string {
  if (weapon === '2h' || weapon === 'staff') return set.twoHand;
  if (weapon === '1h' || weapon === 'dual' || hasShield) return set.oneHand;
  return set.unarmed;
}

export interface CharacterDef {
  id: CharId;
  label: string;
  url: string;                 // GLB (tarayıcı yolu)
  tex: TexClass;
  /** DESIGN §5 weightMult: 0.90 ağır (Barbarian/Knight/Skeleton_Warrior), 1.00 orta, 1.10 hafif iskeletler (±%10 görsel kütle). */
  weightMult: number;
  idle: IdleSet;
  taunt: string;               // Cheering (tS 1.2) / Waving@0-1.4 / Skeletons_Taunt (iskeletler; SP gelene kadar Cheering)
  /** handslot.l/r kemikleri var mı (Mannequin_Medium yok → sanal soket, ANIMATION §7). */
  hasSockets: boolean;
  /** Katalog boyu (u); HUD etiketi yüksekliği için. */
  height: number;
}

const ADV = '/assets/KayKit_Adventurers_2.0_FREE/Characters/gltf/';
const SKEL = '/assets/KayKit_Skeletons_1.1_FREE/characters/gltf/';
const MANQ = '/assets/KayKit_Character_Animations_1.1/Mannequin Character/characters/';

function def(id: CharId, label: string, url: string, tex: TexClass, weightMult: number, taunt: string, hasSockets: boolean, height: number): CharacterDef {
  return { id, label, url, tex, weightMult, idle: IDLE_SET_DEFAULT, taunt, hasSockets, height };
}

/** Sıra = menü sırası; Fighter.charIdx bu diziye indekstir. */
export const CHARACTERS: readonly CharacterDef[] = [
  def('Knight', 'Şövalye', ADV + 'Knight.glb', 'knight', 0.90, CLIPS.CHEERING, true, 2.543),
  def('Barbarian', 'Barbar', ADV + 'Barbarian.glb', 'barbarian', 0.90, CLIPS.CHEERING, true, 2.398),
  def('Mage', 'Büyücü', ADV + 'Mage.glb', 'mage', 1.00, CLIPS.CHEERING, true, 2.655),
  def('Rogue', 'Haydut', ADV + 'Rogue.glb', 'rogue', 1.00, CLIPS.CHEERING, true, 2.18),
  def('Rogue_Hooded', 'Kapüşonlu Haydut', ADV + 'Rogue_Hooded.glb', 'rogue', 1.00, CLIPS.CHEERING, true, 2.173),
  def('Ranger', 'Korucu', ADV + 'Ranger.glb', 'ranger', 1.00, CLIPS.CHEERING, true, 2.275),
  def('Skeleton_Warrior', 'İskelet Savaşçı', SKEL + 'Skeleton_Warrior.glb', 'skeleton', 0.90, CLIPS.SKELETONS_TAUNT, true, 2.166),
  def('Skeleton_Rogue', 'İskelet Haydut', SKEL + 'Skeleton_Rogue.glb', 'skeleton', 1.10, CLIPS.SKELETONS_TAUNT, true, 3.288),
  def('Skeleton_Mage', 'İskelet Büyücü', SKEL + 'Skeleton_Mage.glb', 'skeleton', 1.10, CLIPS.SKELETONS_TAUNT, true, 2.588),
  def('Skeleton_Minion', 'İskelet Uşak', SKEL + 'Skeleton_Minion.glb', 'skeleton', 1.10, CLIPS.SKELETONS_TAUNT, true, 2.166),
  def('Mannequin_Medium', 'Manken', MANQ + 'Mannequin_Medium.glb', 'mannequin', 1.00, CLIPS.WAVING_TAUNT, false, 2.204),
];
export const CHARACTER_BY_ID: Readonly<Record<CharId, CharacterDef>> = Object.fromEntries(CHARACTERS.map((c) => [c.id, c])) as Record<CharId, CharacterDef>;
export const charIndexOf = (id: CharId): number => CHARACTERS.findIndex((c) => c.id === id);
/** Eğitim kuklası: "Manken" (docs/TUTORIAL.md — nötr görünüm, kukla için birebir uygun). */
export const MANNEQUIN_IDX: number = charIndexOf('Mannequin_Medium');
